<script setup lang="ts">
import type { ChosenPropertiesEmits, ChosenPropertiesProps } from './index';

const props = withDefaults(defineProps<ChosenPropertiesProps>(), {
	properties: () => [],
	containerClass: '',
	mainContainerClass: '',
	isLoading: false,
	titleClass: 'text-[28px] md:text-[40px]',
	ctaLink: '/imoveis',
	viewAllText: 'Ver todos'
});

const emit = defineEmits<ChosenPropertiesEmits>();

const clickCount = ref(0);

function handleViewMoreClicked(payload: {
	destination_url: string;
	cta_text: string;
}) {
	clickCount.value += 1;

	emit('cta_view_more_clicked', {
		href: payload.destination_url,
		clickCount: clickCount.value,
		ctaText: props.viewAllText
	});
}

const customCarouselItemClass =
	'basis-[90%] sm:!basis-[49%] md:!basis-[49%] lg:!basis-[32.3%] xl:!basis-[32.3%]';

// Dynamic width calculation for carousel selectors (SSR-safe, CLS-friendly)
const selectorContainerRef = ref<HTMLDivElement | null>(null);
const selectorContainerWidth = ref(0);

const GAP_SIZE = 12;
const MAX_ITEM_WIDTH = 60;

let resizeObserver: ResizeObserver | null = null;

function setupResizeObserver(element: HTMLElement) {
	if (!import.meta.client) return;

	if (resizeObserver) {
		resizeObserver.disconnect();
	}

	resizeObserver = new ResizeObserver(entries => {
		const entry = entries[0];
		if (entry) {
			selectorContainerWidth.value = entry.contentRect.width;
		}
	});

	resizeObserver.observe(element);
}

watch(
	() => selectorContainerRef.value,
	newElement => {
		if (import.meta.client && newElement) {
			setupResizeObserver(newElement);
		}
	},
	{ immediate: true }
);

onMounted(() => {
	if (selectorContainerRef.value) {
		setupResizeObserver(selectorContainerRef.value);
	}
});

onUnmounted(() => {
	if (resizeObserver) {
		resizeObserver.disconnect();
		resizeObserver = null;
	}
});

function computeDynamicWidth(totalSlides: number): number {
	const containerWidth = selectorContainerWidth.value;

	// Return max width during SSR or before ResizeObserver fires (prevents CLS)
	if (!containerWidth || totalSlides <= 0) return MAX_ITEM_WIDTH;

	const totalGapSpace = (totalSlides - 1) * GAP_SIZE;
	const availableSpaceForItems = containerWidth - totalGapSpace;
	const calculatedItemWidth = Math.floor(availableSpaceForItems / totalSlides);

	return Math.min(calculatedItemWidth, MAX_ITEM_WIDTH);
}
</script>

<template>
	<StandardSlider
		:items="properties"
		:view-all-href="ctaLink"
		:view-all-text="viewAllText"
		:is-loading="isLoading"
		:container-class="containerClass"
		:main-container-class="mainContainerClass"
		:carousel-item-class="customCarouselItemClass"
		button-class="w-[231px] md:w-[172px]"
		button-skeleton-class="w-[231px] md:w-[172px] !h-[52px]"
		property-card-variant="transparentBackground"
		section-name="selected properties"
		title="Imóveis selecionados para você"
		selector-variant="white"
		selector-arrows-variant="default"
		:title-classes="titleClass"
		@cta-view-more-clicked="handleViewMoreClicked"
	>
		<template #title>
			<Skeleton
				:is-active="isLoading"
				variant="primary"
				class="h-[42px] w-[302px] lg:h-[54px] lg:w-[380px]"
			>
				<h2
					class="font-matter-sq text-[28px] !leading-normal tracking-[-0.56px] text-white [font-weight:300] lg:text-4xl lg:tracking-[-0.72px]"
				>
					Selecionados para você
				</h2>
			</Skeleton>
			<Skeleton
				:is-active="isLoading"
				variant="primary"
				class="mt-1 h-[21px] w-[401px] lg:h-[27px] lg:w-[420px]"
			>
				<p
					class="font-Inter text-sm font-normal leading-[150%] tracking-normal text-white-dark-2 lg:text-[18px]"
				>
					A partir de imóveis que chamaram sua atenção
				</p>
			</Skeleton>
		</template>

		<template #footer="{ currentIndex, totalSlides, scrollTo }">
			<div
				class="flex h-auto w-full flex-col-reverse items-start justify-between gap-6 md:flex-row lg:items-center"
			>
				<!-- CTA Button -->
				<Skeleton
					:is-active="isLoading"
					variant="primary"
					class="h-[44px] w-[146px] xl:w-[160px] lg:h-[52px]"
				>
					<NuxtLink :to="ctaLink">
						<Button
							size="responsive"
							variant="primary-dark"
							class="text-base font-medium"
							@click="
								handleViewMoreClicked({
									destination_url: ctaLink,
									cta_text: viewAllText
								})
							"
						>
							<span>{{ viewAllText }}</span>
							<IconMaterialSymbolsLightNorthEast class="size-5 xl:size-6" />
						</Button>
					</NuxtLink>
				</Skeleton>

				<!-- Pagination Controls -->
				<div class="flex w-full items-center justify-end gap-5">
					<!-- Pagination Indicators -->
					<div
						ref="selectorContainerRef"
						class="flex w-full flex-wrap justify-end gap-3 md:max-w-[420px]"
					>
						<template v-if="isLoading">
							<div
								v-for="index in 5"
								:key="`skeleton-selector-${index}`"
								class="h-[2px] w-[60px] max-w-[60px] flex-1 bg-primary-light-3"
							/>
						</template>
						<template v-else>
							<CarouselItemSelector
								v-for="(_, index) in totalSlides"
								:key="`selector-${index}`"
								:active="currentIndex === index"
								:delay="2000"
								:dynamic-width="computeDynamicWidth(totalSlides)"
								variant="white"
								@click="scrollTo(index)"
							/>
						</template>
					</div>

					<!-- Navigation Arrows -->
					<div class="flex gap-1">
						<template v-if="isLoading">
							<div
								class="flex size-8 items-center justify-center text-primary-light-3"
							>
								<IconMaterialSymbolsLightChevronLeft class="size-8" />
							</div>
							<div
								class="flex size-8 items-center justify-center text-primary-light-3"
							>
								<IconMaterialSymbolsLightChevronRight class="size-8" />
							</div>
						</template>
						<template v-else>
							<CarouselGenericAction
								variant="default"
								@click="scrollTo(currentIndex - 1)"
							>
								<IconMaterialSymbolsLightChevronLeft
									width="32px"
									height="32px"
								/>
							</CarouselGenericAction>
							<CarouselGenericAction
								variant="default"
								@click="scrollTo(currentIndex + 1)"
							>
								<IconMaterialSymbolsLightChevronRight
									width="32px"
									height="32px"
								/>
							</CarouselGenericAction>
						</template>
					</div>
				</div>
			</div>
		</template>
	</StandardSlider>
</template>
